import React from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useReactFlow,
  type EdgeProps,
  type Edge,
  type Node
} from '@xyflow/react';
import { Tooltip } from '@mantine/core';
import type { RuleNodeData } from '../RuleNode';
import { getHandleColor } from '../../utils/handleColors';
import type { HandleType } from '../../types/registry';
import { isTruncated } from '../../trace/traceShapes';
import type { TraceLabels } from '../../trace/traceShapes';
import type { NodeTraceEntry } from '../../types/editor';
import { TraceValueView } from '../../trace/TraceValueView';

const MISMATCH_COLOR = '#f97316';

type TypedEdgeData = {
  optional?: boolean;
  sourceHandleType?: HandleType;
  targetHandleType?: HandleType;
  /**
   * Full map of per-node trace entries. The edge derives its displayed value at
   * render time from the source node's output via `nodeTraces[edge.source].output`
   * — see `getEdgeTraceValue` below. This replaces the previous top-level
   * `edgeValues` map (dropped in TV1-1491).
   */
  nodeTraces?: Record<string, NodeTraceEntry>;
  showLabel?: boolean;
  traceLabels?: TraceLabels;
};

/**
 * Resolve the value displayed on an edge from the source node's trace output.
 *
 * - If the edge has a named `sourceHandle` (a specific output port), read that
 *   key from the node's output object.
 * - If the edge has no named handle, the node produces a single unnamed output
 *   and the whole output is the edge value.
 *
 * Values larger than the engine leaf cap (16 KB) arrive here as `_truncated`
 * envelopes — `TraceValueView` renders those as a compact chip.
 */
function getEdgeTraceValue(
  nodeTraces: Record<string, NodeTraceEntry> | undefined,
  sourceNodeId: string,
  sourceHandleId: string | null | undefined
): unknown {
  if ( !nodeTraces ) return undefined;
  const entry = nodeTraces[sourceNodeId];
  if ( !entry ) return undefined;
  const output = entry.output;
  if ( !sourceHandleId ) return output;
  if ( output === null || output === undefined || typeof output !== 'object' ) return undefined;
  return ( output as Record<string, unknown> )[sourceHandleId];
}

/**
 * Format a trace value for edge label display.
 */
function formatEdgeValue( value: unknown ): { label: string; isComplex: boolean } {
  if ( value === null || value === undefined ) {
    return { label: String( value ), isComplex: false };
  }
  if ( typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ) {
    const s = String( value );
    return { label: s.length > 30 ? `${s.substring( 0, 30 )}...` : s, isComplex: false };
  }
  return { label: '{...}', isComplex: true };
}

/**
 * Resolve the semantic HandleType for a given node + handle id + direction.
 */
function resolveHandleType(
  nodeId: string,
  handleId: string | null | undefined,
  direction: 'source' | 'target',
  getNode: ( id: string ) => Node | undefined
): HandleType | undefined {
  if ( !handleId ) return undefined;
  const node = getNode( nodeId );
  if ( !node?.data ) return undefined;

  const data = node.data as RuleNodeData;
  const handles = direction === 'source' ? data.outputs : data.inputs;
  const matched = handles?.find(( h ) => h.name === handleId );
  return matched?.type as HandleType | undefined;
}

/**
 * Check if two handle types are compatible.
 * - `any` connects to anything freely.
 * - Same types connect freely.
 * - Different non-any types are a mismatch.
 */
function areTypesCompatible(
  sourceType: HandleType | undefined,
  targetType: HandleType | undefined
): boolean {
  if ( !sourceType || !targetType ) return true;
  if ( sourceType === 'any' || targetType === 'any' ) return true;
  return sourceType === targetType;
}

export function TypedEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  sourceHandleId,
  targetHandleId,
  markerEnd,
  style = {},
  selected,
  data
}: EdgeProps<Edge<TypedEdgeData>> ) {
  const { getNode } = useReactFlow();
  const [ hovered, setHovered ] = React.useState( false );

  const sourceType = resolveHandleType( source, sourceHandleId, 'source', getNode );
  const targetType = resolveHandleType( target, targetHandleId, 'target', getNode );

  const compatible = areTypesCompatible( sourceType, targetType );

  const edgeColor = compatible
    ? ( sourceType ? getHandleColor( sourceType ) : ( style.stroke as string ) || '#64748b' )
    : MISMATCH_COLOR;

  const [ path, labelX, labelY ] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition
  });

  const edgeStyle: React.CSSProperties = {
    ...style,
    stroke: selected ? '#ef4444' : edgeColor,
    strokeWidth: selected ? 3 : 2,
    ...( !compatible && !selected ? { strokeDasharray: '8,4' } : {})
  };

  // Derive the edge's displayed trace value at render time from the source
  // node's output. This replaces the old `data?.traceValue` lookup that was
  // populated upstream via a now-removed `executionTrace.edgeValues` map.
  const traceValue = getEdgeTraceValue( data?.nodeTraces, source, sourceHandleId );
  const hasTraceValue = traceValue !== undefined;
  const showLabel = data?.showLabel || hovered;

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={edgeStyle}
      />
      {/* Invisible wider path for easier hover detection */}
      {hasTraceValue && (
        <path
          d={path}
          fill="none"
          stroke="transparent"
          strokeWidth={20}
          onMouseEnter={() => setHovered( true )}
          onMouseLeave={() => setHovered( false )}
          style={{ pointerEvents: 'stroke' }}
        />
      )}
      {hasTraceValue && showLabel && (
        <EdgeLabelRenderer>
          {(() => {
            const v = traceValue;
            const isSpecialShape = isTruncated( v );

            // Compact label box
            const wrapper = (
              <div
                style={{
                  position: 'absolute',
                  transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                  pointerEvents: 'all',
                  fontSize: 10,
                  fontFamily: '\'JetBrains Mono\', \'Fira Code\', monospace',
                  background: 'rgba(30, 30, 46, 0.85)',
                  color: '#e2e8f0',
                  padding: '2px 6px',
                  borderRadius: 4,
                  whiteSpace: 'nowrap',
                  maxWidth: 240,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  zIndex: 1000
                }}
                onMouseEnter={() => setHovered( true )}
                onMouseLeave={() => setHovered( false )}
              >
                <TraceValueView
                  value={v}
                  compact
                  maxInlineLen={30}
                  traceLabels={data?.traceLabels}
                />
              </div>
            );

            if ( isSpecialShape ) return wrapper;

            const { isComplex } = formatEdgeValue( v );
            if ( isComplex ) {
              return (
                <Tooltip
                  label={JSON.stringify( v, null, 2 )}
                  multiline
                  maw={400}
                  styles={{ tooltip: { fontFamily: '\'JetBrains Mono\', monospace', fontSize: 10, whiteSpace: 'pre-wrap', maxHeight: 300, overflow: 'auto' }}}
                >
                  {wrapper}
                </Tooltip>
              );
            }
            return wrapper;
          })()}
        </EdgeLabelRenderer>
      )}
    </>
  );
}
